import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDownLeft, Eye, EyeOff, Loader2, X } from "lucide-react";
import { createIntouchPayWithdrawal } from "../../api/intouchPayApi";
import {
  getApiErrorMessage,
  getPaymentProviderMessage,
  getAccountPhoneInputValue,
  isPhoneOnlyAccount,
  isPaymentWithdrawalSuccess,
  isValidRwandanPhone,
  normalizePhoneInput,
  parsePaymentAmount,
  PAYMENT_METHODS,
} from "../../utils/paymentHelpers";
import "./payments.css";

const WithdrawModal = ({
  open,
  onClose,
  userId,
  user,
  currentBalance = 0,
  onSuccess,
  onNotify,
}) => {
  const titleId = useId();
  const [amount, setAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("MTN");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isPhoneLocked = isPhoneOnlyAccount(user);

  const numAmount = parsePaymentAmount(amount);
  const exceedsBalance = numAmount != null && numAmount > currentBalance;

  const resetForm = () => {
    setAmount("");
    setPhoneNumber("");
    setPaymentMethod("MTN");
    setPassword("");
    setShowPassword(false);
    setError("");
  };

  useEffect(() => {
    if (!open) return undefined;

    resetForm();
    if (isPhoneOnlyAccount(user) && user?.phoneNumber) {
      setPhoneNumber(getAccountPhoneInputValue(user.phoneNumber));
    }

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    if (loading) return;
    resetForm();
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!numAmount) {
      setError("Enter a valid amount greater than 0.");
      return;
    }

    if (numAmount > currentBalance) {
      setError("Insufficient balance. You can only withdraw up to your current Cash balance.");
      return;
    }

    const effectivePhone = isPhoneLocked
      ? getAccountPhoneInputValue(user?.phoneNumber)
      : phoneNumber;

    if (!effectivePhone.trim()) {
      setError("Enter your mobile money phone number.");
      return;
    }

    if (!isValidRwandanPhone(effectivePhone)) {
      setError("Enter a valid Rwandan phone number.");
      return;
    }

    if (!password) {
      setError("Enter your password to confirm withdrawal.");
      return;
    }

    if (!userId) {
      setError("Sign in again to withdraw.");
      return;
    }

    try {
      setLoading(true);
      const data = await createIntouchPayWithdrawal({
        userId,
        amount: numAmount,
        phoneNumber: normalizePhoneInput(effectivePhone),
        paymentMethod,
        password,
      });

      if (!isPaymentWithdrawalSuccess(data)) {
        const message =
          getPaymentProviderMessage(data) || "Withdrawal could not be started.";
        setError(message);
        onNotify?.("error", "Withdrawal failed", message);
        onSuccess?.(data);
        return;
      }

      const providerMessage = getPaymentProviderMessage(data);
      const payoutSucceeded = data?.withdrawal?.status === "Success";

      onNotify?.(
        "success",
        payoutSucceeded ? "Withdrawal completed" : "Withdrawal initiated",
        providerMessage || "Your payout is being processed."
      );

      onSuccess?.(data);
      handleClose();
    } catch (err) {
      const message = getApiErrorMessage(err, "Unable to initiate withdrawal");
      setError(message);
      onNotify?.("error", "Withdrawal failed", message);
    } finally {
      setLoading(false);
    }
  };

  const submitDisabled =
    loading ||
    !amount ||
    !phoneNumber ||
    !password ||
    exceedsBalance;

  return createPortal(
    <div className="pay-modal-root" role="presentation">
      <button
        type="button"
        className="pay-modal-backdrop"
        aria-label="Close dialog"
        disabled={loading}
        onClick={handleClose}
      />

      <form
        className="pay-modal-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="pay-modal-head">
          <span className="pay-modal-head-icon" aria-hidden>
            <ArrowDownLeft size={16} />
          </span>
          <div className="pay-modal-head-copy">
            <h2 id={titleId}>Withdraw from BetBond</h2>
            <p>Send Cash to your mobile money wallet</p>
          </div>
          <button
            type="button"
            className="pay-modal-close"
            aria-label="Close"
            disabled={loading}
            onClick={handleClose}
          >
            <X size={14} />
          </button>
        </header>

        <div className="pay-modal-body">
          <p className="pay-modal-hint">
            Available Cash: <strong>{Number(currentBalance || 0).toLocaleString()}</strong>
          </p>

          <div className="pay-modal-field">
            <label className="pay-modal-label" htmlFor="pay-withdraw-amount">
              Amount (Cash)
            </label>
            <div className="pay-modal-input-wrap">
              <span className="pay-modal-prefix">Cash</span>
              <input
                id="pay-withdraw-amount"
                type="number"
                min="1"
                step="1"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="Enter amount"
                disabled={loading}
              />
            </div>
            {exceedsBalance ? (
              <p className="pay-modal-error">Amount exceeds available balance.</p>
            ) : null}
          </div>

          <div className="pay-modal-field">
            <label className="pay-modal-label" htmlFor="pay-withdraw-phone">
              Phone number
            </label>
            {isPhoneLocked ? (
              <p className="pay-modal-hint pay-modal-hint-inline">
                Your account phone number is used for mobile money.
              </p>
            ) : null}
            <div className="pay-modal-input-wrap">
              <span className="pay-modal-prefix">+250</span>
              <input
                id="pay-withdraw-phone"
                type="tel"
                inputMode="tel"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                placeholder="0781234567"
                disabled={loading}
                readOnly={isPhoneLocked}
                className={isPhoneLocked ? "pay-modal-input-locked" : undefined}
              />
            </div>
          </div>

          <div className="pay-modal-field">
            <span className="pay-modal-label">Payment method</span>
            <div className="pay-modal-methods" role="radiogroup" aria-label="Payment method">
              {PAYMENT_METHODS.map((method) => (
                <button
                  key={method.id}
                  type="button"
                  className={`pay-modal-method${
                    paymentMethod === method.id ? " pay-modal-method-active" : ""
                  }`}
                  onClick={() => setPaymentMethod(method.id)}
                  disabled={loading}
                  aria-pressed={paymentMethod === method.id}
                >
                  {method.label}
                </button>
              ))}
            </div>
          </div>

          <div className="pay-modal-field">
            <label className="pay-modal-label" htmlFor="pay-withdraw-password">
              Password
            </label>
            <div className="pay-modal-input-wrap">
              <input
                id="pay-withdraw-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
              />
              <button
                type="button"
                className="pay-modal-eye"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          {error ? <p className="pay-modal-error">{error}</p> : null}
        </div>

        <div className="pay-modal-foot">
          <button type="submit" className="pay-modal-submit" disabled={submitDisabled}>
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Processing…
              </>
            ) : (
              "Withdraw"
            )}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default WithdrawModal;
